import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { DisclaimerNodeView } from './view';
import type { ExtensionMenuConfig } from '@cotera/client/app/components/app/rte/types/extension-config';
import type {
  TurnIntoCommand,
  SlashCommand,
} from '@cotera/client/app/components/app/rte/types/extension-interface';

export interface DisclaimerOptions extends ExtensionMenuConfig {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    disclaimer: {
      /**
       * Insert a disclaimer block
       */
      setDisclaimer: (attributes?: { text?: string }) => ReturnType;
      /**
       * Convert current block into a disclaimer
       */
      turnIntoDisclaimer: (attributes?: { text?: string }) => ReturnType;
    };
  }
}

export const Disclaimer = Node.create<DisclaimerOptions>({
  name: 'disclaimer',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  content: 'block+',

  addAttributes() {
    return {
      text: {
        default: '',
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="disclaimer"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['react-component', mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(DisclaimerNodeView);
  },

  addCommands() {
    return {
      setDisclaimer:
        (attributes = {}) =>
        ({ commands, state, chain }) => {
          const { selection } = state;
          const pos = selection.$head.pos;

          // Insert the disclaimer node
          chain()
            .insertContent({
              type: this.name,
              attrs: attributes,
              content: [
                {
                  type: 'paragraph',
                  content: [],
                },
              ],
            })
            .run();

          // Position cursor inside the paragraph within the disclaimer
          // The +2 offset moves us past the disclaimer node and into its first child (paragraph)
          return commands.setTextSelection(pos + 2);
        },
      turnIntoDisclaimer:
        (_attributes = {}) =>
        ({ commands, state, chain }) => {
          const { selection } = state;
          const { $from } = selection;
          const currentNode = $from.node($from.depth);
          const pos = $from.before($from.depth);

          // Extract text content from current node
          const textContent = currentNode.textContent;

          // Delete current node and insert disclaimer with text content wrapped in paragraph
          chain()
            .focus()
            .deleteSelection()
            .insertContent({
              type: this.name,
              content: [
                {
                  type: 'paragraph',
                  content: textContent
                    ? [{ type: 'text', text: textContent }]
                    : [],
                },
              ],
            })
            .run();

          // Position cursor inside the paragraph within the disclaimer
          return commands.setTextSelection(pos + 2);
        },
    };
  },

  // Add turn-into and slash command configurations
  addStorage() {
    return {
      getTurnIntoConfig: (): TurnIntoCommand => ({
        commandName: 'turnIntoDisclaimer',
        label: 'Disclaimer',
        icon: 'text',
        canTurnInto: (editor) => {
          // Can turn into disclaimer if there's a selection and the node is in schema
          return editor.isEditable && editor.can().turnIntoDisclaimer();
        },
        execute: (editor) => {
          editor.chain().focus().turnIntoDisclaimer().run();
        },
      }),
      getSlashConfig: (): SlashCommand => ({
        title: 'Disclaimer',
        subtext: 'Important notice or warning block',
        keywords: ['disclaimer', 'warning', 'notice', 'alert', 'important'],
        badge: 'text',
        group: 'Style',
        available: (editor) => {
          // Check if disclaimer node is in schema and available in slash menu
          return editor.isEditable && editor.can().setDisclaimer();
        },
        action: ({ editor }) => {
          editor.chain().focus().setDisclaimer().run();
        },
      }),
    };
  },
});
